import { ApolloQueryResult } from "@apollo/client";
import { datadogRum } from "@datadog/browser-rum";
import React, { useContext } from "react";

import {
  FeatureFlags,
  FeatureFlagKey,
  parseFeatureFlags,
} from "@stw/feature-flags";

import {
  GetSubaccountAdminFieldsQuery,
  GetSubaccountBaseFieldsQuery,
  GetSubaccountSupervolunteerFieldsQuery,
  LoggedInInfoQuery,
  UserSubaccountRole,
  UserTenantRole,
} from "~frontend/__generated__/graphql";

import { useSubaccount } from "../SubaccountProvider";

export type LoggedInUser = LoggedInInfoQuery["loggedInInfo"]["currentUser"];
export type Tenant = LoggedInInfoQuery["loggedInInfo"]["tenant"];
export type SubaccountBaseData = GetSubaccountBaseFieldsQuery["getSubaccount"];
export type SubaccountSupervolunteerData =
  GetSubaccountSupervolunteerFieldsQuery["getSubaccount"];
export type SubaccountAdminData =
  GetSubaccountAdminFieldsQuery["getSubaccount"];
export type SubaccountRole = UserSubaccountRole | UserTenantRole;
export type UserSubaccountMembership =
  | LoggedInInfoQuery["loggedInInfo"]["subaccountMemberships"][0]
  | LoggedInInfoQuery["loggedInInfo"]["tenantRoleSubaccountMemberships"][0];

export const LoggedInUserContext = React.createContext<{
  loggedInInfo: LoggedInInfoQuery["loggedInInfo"] | null;
  featureFlags: FeatureFlags;
  defaultFeatureFlags: FeatureFlags;
  refetch: () => Promise<ApolloQueryResult<LoggedInInfoQuery>>;
}>({
  loggedInInfo: null,
  featureFlags: parseFeatureFlags({}, {}),
  defaultFeatureFlags: parseFeatureFlags({}, {}),
  refetch: () =>
    Promise.reject(
      new Error("Cannot refetch; not inside a LoggedInUserProvider")
    ),
});

export function useCurrentUser(): LoggedInUser | null {
  const loggedInInfo = useContext(LoggedInUserContext).loggedInInfo;
  const user = loggedInInfo?.currentUser ?? null;
  const subaccountInfo = useSubaccount();

  if (user) {
    let subaccountRole;
    if (subaccountInfo?.subaccount) {
      const currentSubaccount = loggedInInfo?.subaccountMemberships.find(
        membership => membership.subaccount.id === subaccountInfo.subaccount.id
      );
      subaccountRole = currentSubaccount?.role;
    }

    datadogRum.setUser({
      id: user.id,
      tenantRole: user.tenantRole,
      tenantSlug: loggedInInfo?.tenant?.slug,
      subaccountRole,
    });
  }

  return user;
}

export function useCurrentUserIsStaff(): boolean {
  return useCurrentUser()?.tenantRole === UserTenantRole.Staff;
}

export function useCurrentUserHasStwEmail(): boolean {
  const user = useCurrentUser();
  if (!user) {
    return false;
  }

  return (
    user.email.endsWith("@scaletowin.com") ||
    user.email.endsWith("@contractors.scaletowin.com")
  );
}

export function useCurrentUserHasTenantWideAccess(): boolean {
  const currentUser = useCurrentUser();
  return (
    currentUser?.tenantRole === UserTenantRole.Staff ||
    currentUser?.tenantRole === UserTenantRole.Owner
  );
}

export function useTenant(): Tenant | null {
  return useContext(LoggedInUserContext).loggedInInfo?.tenant ?? null;
}

export function useCurrentUserSubaccountMemberships():
  | UserSubaccountMembership[]
  | null {
  const loggedInInfo = useContext(LoggedInUserContext).loggedInInfo;
  if (!loggedInInfo) {
    return null;
  }

  return [
    ...loggedInInfo.subaccountMemberships,
    ...loggedInInfo.tenantRoleSubaccountMemberships,
  ];
}

export function useLoginInfoRefetch(): () => Promise<
  ApolloQueryResult<LoggedInInfoQuery>
> {
  return useContext(LoggedInUserContext).refetch;
}

export function useFeatureFlag<Flag extends FeatureFlagKey>(
  flag: Flag
): FeatureFlags[Flag] {
  const value = useContext(LoggedInUserContext).featureFlags[flag];
  datadogRum.addFeatureFlagEvaluation(flag, value);
  return value;
}

export function useDefaultFeatureFlags(): FeatureFlags {
  return useContext(LoggedInUserContext).defaultFeatureFlags;
}

export function useFreshdeskSSOToken(): string | null {
  return (
    useContext(LoggedInUserContext).loggedInInfo?.freshdeskSSOToken ?? null
  );
}
